const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1706';

// Helper function to transform image paths to full URLs
const transformImagePath = (imagePath) => {
  if (!imagePath) return '';
  
  // If the image is already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // If the image is already in the correct format (img/product/), prepend API URL
  if (imagePath.startsWith('img/product/')) {
    return `${API_URL}/${imagePath}`;
  }
  
  // For backend paths, prepend API URL to make them accessible
  if (!imagePath.startsWith('/')) {
    return `${API_URL}/${imagePath}`;
  }
  
  return `${API_URL}${imagePath}`;
};

// Product transformation function to convert backend data to frontend format
const transformProductData = (backendProduct) => {
  const colors = [
    { id: 'green', label: 'Green', hex: '#8ab76d' },
    { id: 'gray', label: 'Gray', hex: '#9b9b9b' },
    { id: 'light_gray', label: 'Light Gray', hex: '#9b9b9b' },
    { id: 'pink', label: 'Pink', hex: '#ee8989' },
    { id: 'black', label: 'Black', hex: '#000000' },
    { id: 'brique', label: 'Red Brick', hex: '#cb5130' },
    { id: 'red', label: 'Red', hex: '#DC143C' },
    { id: 'rose_pastel', label: 'Rose pastel', hex: '#ee8989' },
  ];

  // Map color names from backend to frontend color IDs
  const colorNameMap = {
    'Vert': 'green',
    'Rouge Brique': 'brique',
    'Rose': 'pink',
    'Rose pastel': 'rose_pastel',
    'Black': 'black',
    'Red': 'red',
    'Gray': 'gray',
    'Green': 'green',
    'Pink': 'pink'
  };

  // Get available colors from colorImages - use actual database color names as labels
  const availableColors = backendProduct.colorImages?.map(colorGroup => {
    const colorId = colorNameMap[colorGroup.name] || colorGroup.name.toLowerCase();
    let colorInfo = colors.find(c => c.id === colorId);

    // If no predefined color found, create a new color object
    if (!colorInfo) {
      // Check if the color has a hex code in the backend data
      const backendColor = backendProduct.colors?.find(c => c.name === colorGroup.name);
      colorInfo = {
        id: colorId,
        label: colorGroup.name,
        hex: backendColor?.code || '#9b9b9b' // Use backend hex code if available, otherwise default to gray
      };
    } else {
      // Use the actual database color name as the label
      colorInfo = { ...colorInfo, label: colorGroup.name };
    }

    // Store the actual image path for this color (transformed to full URL)
    colorInfo.imagePath = colorGroup.images?.[0] ? transformImagePath(colorGroup.images[0]) : '';
    return colorInfo;
  }) || [];

  // Handle products with no color indexing (empty colorImages array)
  if (availableColors.length === 0 && backendProduct.colorImages?.length === 0) {
    // For products without color indexing, create a single default color option
    // This ensures the product can still be displayed properly
    availableColors.push({
      id: 'default',
      label: 'Default',
      hex: '#9b9b9b', // Default gray color
      imagePath: backendProduct.images?.[0] ? transformImagePath(backendProduct.images[0]) : ''
    });
  } else if (backendProduct.colors && backendProduct.colors.length > 0) {
    // If we have colors from backend but no colorImages, create color options from the colors array
    backendProduct.colors.forEach(backendColor => {
      const colorId = colorNameMap[backendColor.name] || backendColor.name.toLowerCase();
      let colorInfo = colors.find(c => c.id === colorId);

      if (!colorInfo) {
        colorInfo = {
          id: colorId,
          label: backendColor.name,
          hex: backendColor.code || '#9b9b9b'
        };
      } else {
        colorInfo = { ...colorInfo, label: backendColor.name };
      }

      colorInfo.imagePath = backendProduct.images?.[0] ? transformImagePath(backendProduct.images[0]) : '';
      availableColors.push(colorInfo);
    });
  }

  // Determine default color (first available)
  const defaultColor = availableColors.length > 0 ? availableColors[0].id : 'gray';

  // Get base name from images (remove color suffix)
  let baseName = 'product';
  if (backendProduct.images && backendProduct.images.length > 0) {
    const imagePath = backendProduct.images[0];
    const filename = imagePath.split('/').pop();
    baseName = filename.replace(/\.(png|jpg|jpeg|webp)$/i, '');
    // Remove color suffix if present (e.g., _green, _pink, etc.)
    baseName = baseName.replace(/_[a-zA-Z]+$/, '');
  }

  // Get primary image - prefer first available color image
  const primaryImage = backendProduct.primaryImage || (backendProduct.colorImages?.[0]?.images?.[0] ? 
    transformImagePath(backendProduct.colorImages[0].images[0]) : 
    (backendProduct.images?.[0] ? transformImagePath(backendProduct.images[0]) : '')
  );

  // Get collection stock information if available
  let collectionData = null;
  let outOfStockColors = [];
  
  // This would normally come from a separate collection fetch or included in product data
  // For now, we'll set placeholder values that can be updated when collection data is available
  if (backendProduct.collectionId) {
    collectionData = {
      id: backendProduct.collectionId,
      isColorIndexed: true, // Default assumption
      colors: availableColors.map(color => ({
        name: color.label,
        stockCombinations: [] // Will be populated from actual collection data
      }))
    };
    
    // Determine out of stock colors based on collection data
    // This is a simplified version - the actual implementation would check real stock data
    outOfStockColors = availableColors
      .filter(color => !collectionData.colors.find(c => c.name === color.label)?.stockCombinations?.some(comb => comb.quantity > 0))
      .map(color => color.id);
  }

  return {
    id: backendProduct._id,
    productId: backendProduct.productId, // Add productId to transformed data
    title: backendProduct.name,
    name: backendProduct.name,
    shortDescription: backendProduct.shortDescription || '',
    description: backendProduct.description || '',
    information: backendProduct.information || '',
    price: `${backendProduct.price}.00 TND`,
    img: primaryImage, // Primary image for display
    // Removed category and subcategory as products only have collections
    collectionId: backendProduct.collectionId,
    collection: collectionData,
    baseName: baseName,
    availableColors: availableColors.map(c => c.id),
    defaultColor: defaultColor,
    outOfStockColors: outOfStockColors,
    colors: availableColors,
    images: backendProduct.images ? backendProduct.images.map(transformImagePath) : [],
    colorImages: backendProduct.colorImages ? backendProduct.colorImages.map(colorGroup => ({
      ...colorGroup,
      images: colorGroup.images ? colorGroup.images.map(transformImagePath) : []
    })) : [],
    primaryImage: primaryImage,
    originalPrice: backendProduct.price
  };
};

// Helper function to handle API responses
const handleApiResponse = async (response) => {
  // Log the response for debugging
  console.log('API Response Status:', response.status);
  console.log('API Response Headers:', response.headers);
  
  // Get the response text first
  const responseText = await response.text();
  console.log('API Response Text (first 500 chars):', responseText.substring(0, 500));
  
  // Check if response is HTML (error page)
  if (responseText.trim().startsWith('<!DOCTYPE') || responseText.trim().startsWith('<html')) {
    console.error('Received HTML instead of JSON:', responseText.substring(0, 200));
    throw new Error('Server returned HTML instead of JSON. Service may be down or misconfigured.');
  }
  
  // Check if response is empty
  if (!responseText || responseText.trim() === '') {
    console.error('Received empty response');
    throw new Error('Server returned empty response');
  }
  
  // Try to parse JSON
  let data;
  try {
    data = JSON.parse(responseText);
  } catch (parseError) {
    console.error('JSON Parse Error:', parseError);
    console.error('Response text:', responseText);
    throw new Error(`Failed to parse JSON response: ${parseError.message}`);
  }
  
  // Check if response indicates an error
  if (!response.ok) {
    const errorMessage = data.message || data.error || `HTTP error! status: ${response.status}`;
    console.error('API Error Response:', data);
    throw new Error(errorMessage);
  }
  
  return data;
};

// Fetch all products with optional filters
export const fetchProducts = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add filters to query params
    if (filters.collectionIds) {
      if (Array.isArray(filters.collectionIds) && filters.collectionIds.length > 0) {
        filters.collectionIds.forEach(id => {
          queryParams.append('collectionId', id);
        });
      } else if (filters.collectionIds) {
        queryParams.append('collectionId', filters.collectionIds);
      }
    } else if (filters.collectionId) {
      queryParams.append('collectionId', filters.collectionId);
    }
    if (filters.search) {
      queryParams.append('q', filters.search);
    }
    if (filters.minPrice) {
      queryParams.append('minPrice', filters.minPrice);
    }
    if (filters.maxPrice) {
      queryParams.append('maxPrice', filters.maxPrice);
    }

    const url = `${API_URL}/products${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    console.log('Fetching products from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include' // Include cookies if needed
    });
    
    const data = await handleApiResponse(response);
    console.log('Parsed products data:', data);
    
    // Handle different possible response structures
    let products = [];
    if (Array.isArray(data)) {
      // Response is directly an array
      products = data;
    } else if (data.data && Array.isArray(data.data)) {
      // Response has a data property with array
      products = data.data;
    } else if (data.products && Array.isArray(data.products)) {
      // Response has a products property with array
      products = data.products;
    } else {
      console.warn('Unexpected response structure:', data);
      products = [];
    }
    
    // Transform backend products to frontend format
    const transformedProducts = products.map(transformProductData);
    
    return {
      success: true,
      data: transformedProducts,
      total: data.total || transformedProducts.length
    };
  } catch (error) {
    console.error('Error fetching products:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Fetch products by collection
export const fetchProductsByCollection = async (collectionId) => {
  try {
    const url = `${API_URL}/products/collection/${collectionId}`;
    console.log('Fetching products by collection from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include'
    });
    
    const data = await handleApiResponse(response);
    
    // Handle different possible response structures
    let products = [];
    if (Array.isArray(data)) {
      products = data;
    } else if (data.data && Array.isArray(data.data)) {
      products = data.data;
    } else if (data.products && Array.isArray(data.products)) {
      products = data.products;
    }
    
    const transformedProducts = products.map(transformProductData);
    
    return {
      success: true,
      data: transformedProducts,
      total: transformedProducts.length
    };
  } catch (error) {
    console.error('Error fetching products by collection:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Search products by name, collection, or category
export const searchProducts = async (query) => {
  try {
    // Enhanced search that can search by name, collection, or category
    const url = `${API_URL}/products/search?q=${encodeURIComponent(query)}`;
    console.log('Searching products from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include'
    });

    const data = await handleApiResponse(response);

    // Handle different possible response structures
    let products = [];
    if (Array.isArray(data)) {
      products = data;
    } else if (data.data && Array.isArray(data.data)) {
      products = data.data;
    } else if (data.products && Array.isArray(data.products)) {
      products = data.products;
    }

    const transformedProducts = products.map(transformProductData);

    return {
      success: true,
      data: transformedProducts,
      total: transformedProducts.length
    };
  } catch (error) {
    console.error('Error searching products:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Fetch single product by ID
export const fetchProductById = async (id) => {
  try {
    const url = `${API_URL}/products/${id}`;
    console.log('Fetching product by ID from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include'
    });
    
    const data = await handleApiResponse(response);
    
    // Handle different possible response structures
    let product = null;
    if (data.data) {
      product = data.data;
    } else if (data.product) {
      product = data.product;
    } else if (data._id) {
      // Response is the product itself
      product = data;
    }
    
    if (!product) {
      throw new Error('Product not found in response');
    }
    
    const transformedProduct = transformProductData(product);
    
    return {
      success: true,
      data: transformedProduct
    };
  } catch (error) {
    console.error('Error fetching product by ID:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// Fetch single product by productId (new randomly generated ID)
export const fetchProductByProductId = async (productId) => {
  try {
    const url = `${API_URL}/products/by-product-id/${productId}`;
    console.log('Fetching product by productId from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include'
    });

    const data = await handleApiResponse(response);

    // Handle different possible response structures
    let product = null;
    if (data.data) {
      product = data.data;
    } else if (data.product) {
      product = data.product;
    } else if (data._id) {
      // Response is the product itself
      product = data;
    }

    if (!product) {
      throw new Error('Product not found in response');
    }

    const transformedProduct = transformProductData(product);

    return {
      success: true,
      data: transformedProduct
    };
  } catch (error) {
    console.error('Error fetching product by productId:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};